import ApiService from "@/services/api.service";

const state = {
  loading: false,
  inviiMassivi: [],
  error: null,
};

const mutations = {
  setLoading(state, loading) {
    state.loading = loading;
  },
  setInviiMassivi(state, inviiMassivi) {
    state.inviiMassivi = inviiMassivi || [];
  },
  setError(state, error) {
    state.error = error;
  },
  removeInvio(state, invioId) {
    state.inviiMassivi = state.inviiMassivi.filter(invio => invio && invio.id !== invioId);
  },
  setInvioDownloading(state, { invioId, isDownloading }) {
    const invio = state.inviiMassivi.find(item => item && item.id === invioId);
    if (invio) {
      invio.isDownloading = isDownloading;
    }
  },
};

const actions = {
  async FETCH_INVII_MASSIVI({ commit }) {
    commit("setLoading", true);
    commit("setError", null);

    try {
      const response = await ApiService.get("MailMassiveUI/elenco-invii");
      
      // Transform the response data to match our component expectations
      const transformedData = (response.data || []).filter(item => item).map(item => ({
        id: item.id || `temp_${Date.now()}_${Math.random()}`,
        campaignName: item.campaignName || item.nomeCampagna || "N/A",
        sentAt: item.sentAt || item.dataInvio || new Date().toISOString(),
        status: item.status || item.stato || "unknown",
        totalCount: Number(item.totalCount || item.totaleInvii || 0),
        successCount: Number(item.successCount || item.inviiRiusciti || 0),
        failureCount: Number(item.failureCount || item.inviiFalliti || 0),
        description: item.description || item.descrizione || "",
        errorMessage: item.errorMessage || item.messaggioErrore || "",
        clusterId: item.clusterId || null, // Add clusterId field (nullable)
        isDownloading: false, // Frontend-only property for UI state
      }));

      commit("setInviiMassivi", transformedData);
    } catch (error) {
      commit("setError", error.response?.data?.message || "Errore nel caricamento dei dati");
      throw error;
    } finally {
      commit("setLoading", false);
    }
  },

  async DOWNLOAD_REPORT({ commit }, { invioId, campaignName, clusterId }) {
    commit("setError", null);
    commit("setInvioDownloading", { invioId, isDownloading: true });

    try {
      // Build API URL - add clusterId as query parameter if it exists
      let apiUrl = `MailMassiveUI/download-report/${campaignName}`;
      if (clusterId) {
        apiUrl += `?clusterId=${encodeURIComponent(clusterId)}`;
      }
      
      const response = await ApiService.downloadFile(apiUrl);

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      
      // Extract filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `report_${clusterId}.xlsx`;

      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response;
    } catch (error) {
      commit("setError", error.response?.data?.message || "Errore nel download del report");
      throw error;
    } finally {
      commit("setInvioDownloading", { invioId, isDownloading: false });
    }
  },

  async DELETE_INVIO({ commit }, invioId) {
    commit("setError", null);

    try {
      await ApiService.delete(`MailMassiveUI/delete-invio/${invioId}`);
      commit("removeInvio", invioId);
    } catch (error) {
      commit("setError", error.response?.data?.message || "Errore nell'eliminazione dell'invio");
      throw error;
    }
  },
};

const getters = {
  getInvioById: (state) => (id) => {
    return state.inviiMassivi.find(invio => invio.id === id);
  },
  
  getInviiByStatus: (state) => (status) => {
    return state.inviiMassivi.filter(invio => invio.status === status);
  },
  
  getTotalInvii: (state) => {
    return state.inviiMassivi.length;
  },
  
  getCompletedInvii: (state) => {
    return state.inviiMassivi.filter(invio => invio.status === 'completed').length;
  },
  
  getProcessingInvii: (state) => {
    return state.inviiMassivi.filter(invio => invio.status === 'processing').length;
  },
  
  getFailedInvii: (state) => {
    return state.inviiMassivi.filter(invio => invio.status === 'failed').length;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
